import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  Background,
  Controls,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type EdgeChange,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useHasPermission } from '@/auth/auth-store';
import {
  usePublishScreenFlow,
  useSaveScreenFlow,
  useScreenFlow,
  useScreenManifest,
} from '@/hooks/use-screen-flows';
import { useT } from '@/i18n/use-t';
import { ScreenNode, type ScreenFlowRfNode } from './screen-node';
import {
  journeyEdgeView,
  newId,
  screenById,
  validateJourneyGraph,
  type GraphIssue,
  type JourneyGraph,
  type ScreenNodeDef,
} from './screen-flow-types';

function extractIssues(err: unknown): { message: string; issues: GraphIssue[] } {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; issues?: GraphIssue[] } | undefined;
    return { message: body?.message ?? err.message, issues: body?.issues ?? [] };
  }
  return { message: err instanceof Error ? err.message : 'Unexpected error', issues: [] };
}

const nodeTypes = { screen: ScreenNode };

export function JourneyEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: flow, isLoading } = useScreenFlow(id);
  const { data: manifest } = useScreenManifest(flow?.surface);
  const save = useSaveScreenFlow();
  const publish = usePublishScreenFlow();
  const canWrite = useHasPermission('screen-flows.write');
  const t = useT('admin_ui');

  const [nodes, setNodes, onNodesChange] = useNodesState<ScreenFlowRfNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [name, setName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<GraphIssue[]>([]);
  const lastInit = useRef<{ id: string; version: number } | null>(null);

  // (Re)initialise from the loaded flow only when its identity/version
  // changes — local edits between saves are preserved.
  useEffect(() => {
    if (flow === undefined || manifest === undefined) return;
    if (lastInit.current?.id === flow.id && lastInit.current.version === flow.version) return;
    const byScreen = screenById(manifest);
    const screenOf = new Map(flow.graph.nodes.map((n) => [n.nodeId, n.screenId]));
    setName(flow.name);
    setNodes(
      flow.graph.nodes.map((n) => {
        const def = byScreen.get(n.screenId);
        return {
          id: n.nodeId,
          type: 'screen' as const,
          position: n.position,
          data: {
            label: def?.label ?? n.screenId,
            screenId: n.screenId,
            isStart: def?.isStart ?? false,
            isTerminal: def?.isTerminal ?? false,
            events: def?.events ?? [],
          },
        };
      }),
    );
    setEdges(
      flow.graph.edges.map((e) => journeyEdgeView(e, byScreen.get(screenOf.get(e.source) ?? ''))),
    );
    setDirty(false);
    setServerIssues([]);
    lastInit.current = { id: flow.id, version: flow.version };
  }, [flow, manifest, setNodes, setEdges]);

  const handleNodesChange = useCallback(
    (changes: NodeChange<ScreenFlowRfNode>[]) => {
      onNodesChange(changes);
      if (changes.some((c) => c.type === 'position' || c.type === 'remove')) setDirty(true);
    },
    [onNodesChange],
  );
  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      onEdgesChange(changes);
      if (changes.some((c) => c.type === 'remove')) setDirty(true);
    },
    [onEdgesChange],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      if (c.source === null || c.target === null || c.sourceHandle === null) return;
      const sourceScreenId = nodes.find((n) => n.id === c.source)?.data.screenId;
      const sourceScreen =
        manifest !== undefined && sourceScreenId !== undefined
          ? screenById(manifest).get(sourceScreenId)
          : undefined;
      const edge = journeyEdgeView(
        { id: newId('e'), source: c.source, target: c.target, sourceEvent: c.sourceHandle },
        sourceScreen,
      );
      setEdges((eds) => addEdge<Edge>(edge, eds));
      setDirty(true);
    },
    [nodes, manifest, setEdges],
  );

  function addScreen(def: ScreenNodeDef): void {
    setNodes((ns) => [
      ...ns,
      {
        id: newId('n'),
        type: 'screen' as const,
        position: { x: 80, y: 60 + ns.length * 30 },
        data: {
          label: def.label,
          screenId: def.id,
          isStart: def.isStart,
          isTerminal: def.isTerminal,
          events: def.events,
        },
      },
    ]);
    setDirty(true);
  }

  const toGraph = useCallback((): JourneyGraph => {
    return {
      manifestVersion: manifest?.manifestVersion ?? flow?.manifestVersion ?? '',
      nodes: nodes.map((n) => ({
        nodeId: n.id,
        screenId: n.data.screenId,
        position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        sourceEvent: e.sourceHandle ?? '',
        target: e.target,
      })),
    };
  }, [nodes, edges, manifest, flow]);

  const liveIssues = useMemo(
    () => (manifest !== undefined ? validateJourneyGraph(toGraph(), manifest) : []),
    [toGraph, manifest],
  );

  if (isLoading)
    return (
      <p className="text-sm text-muted-foreground">
        {t('screenFlows.editor.loading', 'Loading journey…')}
      </p>
    );
  if (flow === undefined)
    return (
      <p className="text-sm text-destructive">
        {t('screenFlows.editor.notFound', 'Journey not found.')}
      </p>
    );

  async function onSave(): Promise<void> {
    setBanner(null);
    setOkMsg(null);
    setServerIssues([]);
    try {
      const updated = await save.mutateAsync({
        id: flow!.id,
        expectedVersion: flow!.version,
        name,
        graph: toGraph(),
      });
      setOkMsg(
        t('screenFlows.editor.saved', 'Saved (v{version}).').replace(
          '{version}',
          String(updated.version),
        ),
      );
      setDirty(false);
    } catch (err) {
      setBanner(extractIssues(err).message);
    }
  }

  async function onPublish(): Promise<void> {
    setBanner(null);
    setOkMsg(null);
    setServerIssues([]);
    try {
      const updated = await publish.mutateAsync(flow!.id);
      setOkMsg(
        t(
          'screenFlows.editor.published',
          'Published (v{version}). Terminals pick it up on next sync.',
        ).replace('{version}', String(updated.version)),
      );
      setDirty(false);
    } catch (err) {
      const { message, issues } = extractIssues(err);
      setBanner(message);
      setServerIssues(issues);
    }
  }

  const issues = serverIssues.length > 0 ? serverIssues : liveIssues;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <Link to="/screen-flows" className="text-xs text-muted-foreground hover:underline">
          ← {t('screenFlows.title', 'Screen flows')}
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">{flow.name}</h1>
          <Badge variant="outline">{flow.surface}</Badge>
          <Badge variant={flow.status === 'published' ? 'success' : 'default'}>{flow.status}</Badge>
          <span className="text-xs text-muted-foreground">
            v{flow.version} · {t('screenFlows.editor.manifest', 'manifest')} {flow.manifestVersion}
          </span>
        </div>
      </header>

      {canWrite && (
        <div className="max-w-md space-y-1">
          <label className="text-xs text-muted-foreground">{t('screenFlows.name', 'Name')}</label>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            maxLength={200}
          />
        </div>
      )}

      {banner !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {banner}
        </p>
      )}
      {okMsg !== null && (
        <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
          {okMsg}
        </p>
      )}

      <div className="grid grid-cols-[200px_1fr] gap-3">
        {/* Palette */}
        <aside className="space-y-2 rounded-card border border-border bg-card p-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('screenFlows.editor.screens', 'Screens')}
          </h2>
          {manifest === undefined ? (
            <p className="text-xs text-muted-foreground">
              {t('screenFlows.editor.loadingPalette', 'Loading palette…')}
            </p>
          ) : (
            manifest.screens.map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={!canWrite}
                onClick={() => addScreen(s)}
                className="block w-full rounded border border-border px-2 py-1 text-left text-xs hover:border-primary disabled:opacity-50"
                title={s.id}
              >
                {s.label}
                {s.isStart && (
                  <span className="ml-1 text-[10px] text-sky-500">
                    {t('screenFlows.startBadge', 'start')}
                  </span>
                )}
                {s.isTerminal && (
                  <span className="ml-1 text-[10px] text-emerald-500">
                    {t('screenFlows.endBadge', 'end')}
                  </span>
                )}
              </button>
            ))
          )}
        </aside>

        {/* Canvas */}
        <div className="h-[60vh] rounded-card border border-border" style={{ minHeight: 420 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            nodesConnectable={canWrite}
            edgesFocusable={canWrite}
          >
            <Background />
            <Controls />
          </ReactFlow>
        </div>
      </div>

      {/* Validation panel */}
      <section className="rounded-card border border-border bg-card p-3">
        <h2 className="text-sm font-medium">
          {t('screenFlows.editor.validation', 'Validation')}{' '}
          {issues.length === 0
            ? t('screenFlows.editor.validationValid', '· ✓ valid')
            : t('screenFlows.editor.validationIssues', '· {count} issue(s)').replace(
                '{count}',
                String(issues.length),
              )}
        </h2>
        {issues.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-destructive">
            {issues.map((i, idx) => (
              <li key={idx}>
                <span className="font-mono">{i.code}</span> — {i.message}
              </li>
            ))}
          </ul>
        )}
      </section>

      {canWrite && (
        <div className="flex items-center gap-2">
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending
              ? t('common.saving', 'Saving…')
              : t('screenFlows.editor.saveDraft', 'Save draft')}
          </Button>
          <Button
            variant="outline"
            onClick={onPublish}
            disabled={publish.isPending || dirty || liveIssues.length > 0}
          >
            {publish.isPending
              ? t('screenFlows.editor.publishing', 'Publishing…')
              : t('screenFlows.editor.publish', 'Publish')}
          </Button>
          {dirty && (
            <span className="text-xs text-muted-foreground">
              {t('screenFlows.editor.saveBeforePublishing', 'Save before publishing.')}
            </span>
          )}
          {!dirty && liveIssues.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {t('screenFlows.editor.fixIssuesToPublish', 'Fix validation issues to publish.')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
